import '../../styles/Spinner.css'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Col,
  Form,
  FormGroup,
  InputGroupText,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap'
import { FaCopy, FaSearch, FaTrash } from 'react-icons/fa'
import { Card, Container, FormControl, InputGroup, ToggleButton, ToggleButtonGroup } from 'react-bootstrap'
import InfiniteScroll from 'react-infinite-scroll-component'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { User } from 'context/UserContext'
import { useAuth } from 'context/AuthContext'
import { FaArrowLeft } from 'react-icons/fa6'
import { ToastOptionsDefault } from 'utils/toastOptions'

const GalleryContainer = styled(Container)`
  margin-top: 2rem;
`

const ScreenshotCard = styled(Card)`
  margin-bottom: 1rem;
  cursor: pointer;
`

const ToggleButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  gap: 10px;
`

const UserGallery: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [screenshots, setScreenshots] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1) // Initial page for fetching data
  const [hasMore, setHasMore] = useState(true) // Flag to indicate if more pages exist
  const [isFetching, setIsFetching] = useState(false) // Single flag for fetching state
  const [total, setTotal] = useState(0)
  const [totalSize, setTotalSize] = useState(0)
  const [user, setUser] = useState<User | null>(null)
  const { token } = useAuth()
  const navigate = useNavigate()
  const urlRef = useRef('')
  const [selectedScreenshotId, setSelectedScreenshotId] = useState<string | null>(null)
  
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const toggleDeleteModal = () => setDeleteModal(!deleteModal)
  
  const [searchTerm, setSearchTerm] = useState<string>('')
  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`/api/admin/users/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const data = await response.json()
        setUser(data)
      } catch (err) {
        console.error('Failed to fetch user', err)
      }
    }
    fetchUser()
  }, [id, token])
  
  const fetchScreenshots = useCallback(async () => {
    if (isFetching || !hasMore) return
    
    setIsFetching(true)
    
    try {
      const response = await fetch(`/api/admin/users/${id}/gallery?page=${page}&limit=25`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      
      if (!response.ok) {
        if (response.status === 404) {
          const data = await response.json()
          if (data.message === 'No screenshots found for this user') {
            setHasMore(false)
          }
        } else {
          toast.error('Failed to retrieve screenshots. Please retry later.', ToastOptionsDefault)
        }
      } else {
        const data = await response.json()
        setScreenshots((prevScreenshots) => [...prevScreenshots, ...data.screenshots])
        setHasMore(data.currentPage < data.totalPages)
        setPage((prevPage) => prevPage + 1)
        setTotal(data.total)
        setTotalSize(data.totalSize)
      }
    } catch (err) {
      toast.error('Failed to retrieve screenshots. Please retry later.', ToastOptionsDefault)
      setError('Failed to fetch screenshots')
    } finally {
      setIsFetching(false)
    }
  }, [page, hasMore, isFetching])
  
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      urlRef.current = 'http://localhost:3000/'
    }
    
    if (!isFetching) {
      fetchScreenshots()
    }
  }, [])
  
  const handlePrivacyToggle = async (id: string, privacy: string) => {
    try {
      const response = await fetch(`/api/admin/screenshot/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ privacy: privacy === 'public' ? 'private' : 'public' }),
      })
      
      if (!response.ok) {
        toast.error(`Failed to update privacy. Please retry later. (${response.statusText})`, ToastOptionsDefault)
      } else {
        const updatedScreenshot = await response.json()
        setScreenshots(screenshots.map(screenshot => screenshot.id === updatedScreenshot.id ? updatedScreenshot : screenshot))
        toast.success(`Screenshot privacy has successfully been changed to ${privacy === 'public' ? 'private' : 'public'}.`,
          ToastOptionsDefault
        )
      }
    } catch (err) {
      setError('Failed to update privacy')
    }
  }
  
  const handleDelete = async (id: string) => {
    try {
      toggleDeleteModal()
      const response = await fetch(`/api/admin/screenshot/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
      
      if (!response.ok) {
        toast.error(`Failed to delete screenshot. Please retry later. (${response.statusText})`, ToastOptionsDefault)
      } else {
        setScreenshots(screenshots.filter(screenshot => screenshot.id !== id))
        setTotal(total - 1)
        toast.success('Screenshot deleted successfully !', ToastOptionsDefault)
      }
    } catch (err) {
      setError('Failed to delete screenshot')
    }
  }
  
  const filteredScreenshots = screenshots.filter(screenshot =>
    screenshot.title.toLowerCase().includes(searchTerm.toLowerCase())
  )
  
  if (error) {
    return <div className="alert alert-danger">{error}</div>
  }
  
  return (
    <>
      <div className="position-relative">
        <section className="section section-hero section-shaped">
          <div className="shape shape-style-1 shape-default">
            <span className="span-150"/>
            <span className="span-50"/>
            <span className="span-50"/>
            <span className="span-75"/>
            <span className="span-100"/>
            <span className="span-75"/>
            <span className="span-50"/>
            <span className="span-100"/>
            <span className="span-50"/>
            <span className="span-100"/>
          </div>
          <GalleryContainer id="galleryContainer">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Button variant="default" href={ '/admin/users/' + id }>
                <FaArrowLeft/> Back on { user?.nickname }&apos;s profile
              </Button>
              <Form className="navbar-search navbar-search-dark form-inline">
                <FormGroup className="mb-0">
                  <InputGroup className="input-group-alternative">
                    <InputGroupText>
                      <FaSearch/>
                    </InputGroupText>
                    <FormControl
                      placeholder="Search by name"
                      value={ searchTerm }
                      onChange={ (e) => setSearchTerm(e.target.value) }
                    />
                  </InputGroup>
                </FormGroup>
              </Form>
            </div>
            <h1>{ user?.nickname }&apos;s Gallery ({ total } screenshots - { totalSize }mb)</h1>
            
            { filteredScreenshots.length > 0 ? (
              <InfiniteScroll
                dataLength={ filteredScreenshots.length }
                next={ fetchScreenshots }
                hasMore={ hasMore }
                loader={ <div>Loading...</div> }
                endMessage={ <p style={ { textAlign: 'center' } }>Yay ! You have seen it all</p> }
              >
                <Row>
                  { filteredScreenshots.map((screenshot) => (
                    <Col md={ 4 } key={ screenshot.id }>
                      <ScreenshotCard style={ { backgroundColor: screenshot.deleted ? 'red': 'transparent' } }>
                        <Card.Img
                          onClick={ () => navigate(`/${ screenshot.url }`) }
                          variant="top"
                          src={ `/${ screenshot.path }` }
                          alt={ screenshot.title }
                        />
                        <Card.Body>
                          <Card.Title>{ screenshot.title }</Card.Title>
                          <Card.Text>Views: { screenshot.views } / Size: { screenshot.size }mb</Card.Text>
                          <Card.Text>
                            <i>{ new Date(screenshot.createdAt).toLocaleString('en-EN', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                            }) }</i>
                          </Card.Text>
                          { !screenshot.deleted && (
                            <>
                              <ToggleButtonContainer>
                                <CopyToClipboard text={ `${ 'https://screen-me.cloud/' + screenshot.url }` }>
                                  <Button
                                    color="success"
                                    className="mr-2"
                                    onClick={ () => {
                                      toast.success('Screenshot URL copied to your clipboard !',
                                        ToastOptionsDefault,
                                      )
                                    } }>
                                    <FaCopy/>
                                  </Button>
                                </CopyToClipboard>
                                <ToggleButtonGroup
                                  type="radio"
                                  name={ `privacyOptions-${ screenshot.id }` }
                                  value={ screenshot.privacy }
                                  onChange={ () => handlePrivacyToggle(screenshot.id, screenshot.privacy) }
                                >
                                  <ToggleButton id={ `public-${ screenshot.id }` } variant="outline-secondary"
                                    value="public">Public</ToggleButton>
                                  <ToggleButton id={ `private-${ screenshot.id }` } variant="outline-secondary"
                                    value="private">Private</ToggleButton>
                                </ToggleButtonGroup>
                                <Button color="danger" onClick={ (e) => {
                                  e.stopPropagation()
                                  setSelectedScreenshotId(screenshot.id)
                                  toggleDeleteModal()
                                } }><FaTrash/></Button>
                              </ToggleButtonContainer>
                            </>
                          ) }
                        </Card.Body>
                      </ScreenshotCard>
                    </Col>
                  )) }
                </Row>
              </InfiniteScroll>
            ): (
              <p style={ { textAlign: 'center' } }>No screenshots found matching your search.</p>
            ) }
          </GalleryContainer>
          <Modal isOpen={ deleteModal } toggle={ toggleDeleteModal }>
            <ModalHeader toggle={ toggleDeleteModal }>
              <h2>Confirm Delete Screenshot</h2>
            </ModalHeader>
            <ModalBody>
              <p>
                Are you sure you want to delete this screenshot ?
              </p>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" onClick={ () => {
                if (selectedScreenshotId) {
                  handleDelete(selectedScreenshotId)
                }
              } }>
                Confirm Delete
              </Button>{ ' ' }
              <Button color="secondary" onClick={toggleDeleteModal}>
                Cancel
              </Button>
            </ModalFooter>
          </Modal>
          <div className="separator separator-bottom separator-skew zindex-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
            >
              <polygon
                className="fill-white"
                points="2560 0 2560 100 0 100"
              />
            </svg>
          </div>
        </section>
      </div>
    </>
  )
}

export default UserGallery
